/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { BehaviorSubject } from "rxjs";
import { ScaleTime } from "d3";
import * as d3 from "d3";
import { Group } from "@iventis/types";
import { Session, SessionComponent } from "./types";
import { GroupScale } from "./group-axis";

// all values are in pixels
export const TIME_AXIS_WIDTH = 40;
export const CATEGORY_ROW_HEIGHT = 25;
export const SESSION_RECT_PADDING = 4;

export enum InteractionPhase {
    START,
    END,
}

export type CalendarTheme = { iconButtonColours: { idle: string; selected: string } };

export type CalendarState = "formatting-calendar" | "idle";

export interface ContextPropsData {
    sessions: Session[];
    groups: Group[];
    start: Date;
    end: Date;
    sessionComponent: SessionComponent;
    eventUnitComponent: SessionComponent;
    sessionTabFormatter?: (session: Session) => string;
    minCategoryWidth: number;
    showSubEvents: boolean;
}

export interface ViewportSizeData {
    height: number;
    width: number;
}

export enum CalendarCursor {
    DEFAULT = "default",
    GRAB = "grab",
    GRABBING = "grabbing",
    POINTER = "pointer",
}

export class ContextData {
    private calendarPropsSubject$ = new BehaviorSubject<ContextPropsData>(undefined);

    private zoomSubject$ = new BehaviorSubject<d3.D3ZoomEvent<SVGGraphicsElement, unknown>>(undefined);

    private interactionSubject$ = new BehaviorSubject<InteractionPhase>(undefined);

    private viewportSizeSubject$ = new BehaviorSubject<ViewportSizeData>(undefined);

    private timeScaleSubject$ = new BehaviorSubject<ScaleTime<number, number, never>>(undefined);

    private groupScaleSubject$ = new BehaviorSubject<GroupScale>(undefined);

    private groupAxisHeightSubject$ = new BehaviorSubject<number>(0);

    private cursorSubject$ = new BehaviorSubject<CalendarCursor>(CalendarCursor.DEFAULT);

    private stateSubject$ = new BehaviorSubject<CalendarState>("formatting-calendar");

    private theme: CalendarTheme;

    get calendarProps$() {
        return this.calendarPropsSubject$;
    }

    get viewportSize$() {
        return this.viewportSizeSubject$;
    }

    get zoom$() {
        return this.zoomSubject$;
    }

    get timeScale$() {
        return this.timeScaleSubject$;
    }

    get groupScale$() {
        return this.groupScaleSubject$;
    }

    get groupAxisHeight$() {
        return this.groupAxisHeightSubject$;
    }

    get interactionPhase$() {
        return this.interactionSubject$;
    }

    get cursor$() {
        return this.cursorSubject$;
    }

    get state$() {
        return this.stateSubject$;
    }

    get getTheme() {
        return this.theme;
    }

    updateCalendarProps(
        sessions: Session[],
        groups: Group[],
        start: Date,
        end: Date,
        minCategoryWidth: number,
        sessionComponent: SessionComponent,
        eventUnitComponent: SessionComponent,
        sessionTabFormatter?: (session: Session) => string,
        showSubEvents?: boolean
    ) {
        this.calendarPropsSubject$.next({ sessions, groups, start, end, minCategoryWidth, showSubEvents, sessionComponent, eventUnitComponent, sessionTabFormatter });
    }

    updateGroupAxisHeight = (axisHeight: number) => {
        this.groupAxisHeightSubject$.next(axisHeight);
    };

    updateInteractionPhase = (phase: InteractionPhase) => {
        this.interactionSubject$.next(phase);
    };

    updateZoom(zoomEvent: d3.D3ZoomEvent<SVGGraphicsElement, unknown>) {
        this.zoomSubject$.next(zoomEvent);
    }

    updateTimeScale = (scale: ScaleTime<number, number, never>) => {
        this.timeScaleSubject$.next(scale);
    };

    updateGroupScale = (scale: GroupScale) => {
        this.groupScaleSubject$.next(scale);
    };

    updateViewportSize(height: number, width: number) {
        this.viewportSizeSubject$.next({ height, width });
    }

    updateCursor(cursor: CalendarCursor) {
        if (this.interactionPhase$.value !== InteractionPhase.START && this.cursorSubject$.value !== cursor) {
            this.cursorSubject$.next(cursor);
        }
    }

    updateTheme(theme: CalendarTheme) {
        this.theme = theme;
    }

    updateState(state: CalendarState) {
        this.stateSubject$.next(state);
    }
}

export const CalendarContext = React.createContext<ContextData>(undefined);
